import React, {useMemo, useState} from 'react';
import {Community} from '../../models/community';
import {Modal, ModalSize} from '../modals/modal';
import {CommunityBadgeFromCommunity} from '../community-badge';

const SEARCH_THRESHOLD = 20;

interface CommunityBadgesModalProps {
  communities: Community[];
  isOpen: boolean;
  onClose: () => void;
}

export const CommunityBadgesModal: React.FC<CommunityBadgesModalProps> = ({communities, isOpen, onClose}) => {
  const [search, setSearch] = useState('');
  const showSearch = communities.length > SEARCH_THRESHOLD;

  const filtered = useMemo(() => {
    if (!search.trim()) return communities;
    const q = search.trim().toLowerCase();
    return communities.filter(c => (c.name ?? '').toLowerCase().includes(q) || (c.description ?? '').toLowerCase().includes(q));
  }, [communities, search]);

  if (!isOpen) return null;

  return (
    <Modal
      className="max-w-2xl transition duration-200 ease-out"
      isOpen={isOpen}
      onClose={onClose}
      preventBackdropClose={false}
      showCloseButton={true}
      size={ModalSize.LG}>
      <div className="p-6">
        <h2 className="text-xl font-semibold text-gray-900">Communities ({communities.length})</h2>
        <p className="text-sm text-gray-500 mt-1">Only members of these communities can view and apply to this job.</p>

        {showSearch && (
          <div className="mt-4">
            <input
              type="search"
              placeholder="Search communities..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              aria-label="Search communities"
            />
          </div>
        )}

        <div className="mt-4 max-h-[60vh] overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="text-sm text-gray-500 py-4">
              {showSearch && search.trim() ? 'No communities match your search.' : 'No communities.'}
            </p>
          ) : (
            <ul className="grid gap-3 sm:grid-cols-2">
              {filtered.map(community => (
                <li key={community.id} className="flex items-start gap-3 rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                  <div className="flex-shrink-0">
                    <CommunityBadgeFromCommunity community={community} size={40} variant="shield" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-gray-900 text-sm truncate" title={community.name || community.id}>
                      {community.name || community.id}
                    </p>
                    {community.memberCount > 0 && (
                      <p className="text-xs text-gray-600 mt-0.5">
                        {community.memberCount.toLocaleString()} member{community.memberCount === 1 ? '' : 's'}
                      </p>
                    )}
                    {community.description && <p className="text-xs text-gray-500 mt-1 line-clamp-2">{community.description}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </Modal>
  );
};
